// SPDX-License-Identifier: AGPL-3.0-or-later
/**
 * End-to-end encryption for Almanac. Everything here runs in the browser;
 * the server only ever sees the outputs (wrapped keys and ciphertext).
 *
 * Key hierarchy (see "Encryption design" in the scope document):
 *   passphrase --Argon2id--> key-encryption key --wraps--> master key
 *   recovery key ---------------------------------wraps--> master key
 *   master key --wraps--> one random key per area (budgets, investments, …)
 *   area key --encrypts--> records in that area
 *   area key --sealed to a connection's X25519 public key--> sharing
 */
import sodiumModule from "libsodium-wrappers-sumo";
let sodium;
/** Must be awaited once before any other function is used. */
export async function ready() {
    await sodiumModule.ready;
    sodium = sodiumModule;
}
function s() {
    if (!sodium)
        throw new Error("Call ready() from @almanac/crypto first.");
    return sodium;
}
export const KEY_BYTES = 32;
export function toBase64(bytes) {
    return s().to_base64(bytes, s().base64_variants.URLSAFE_NO_PADDING);
}
export function fromBase64(text) {
    return s().from_base64(text, s().base64_variants.URLSAFE_NO_PADDING);
}
export function randomKey() {
    return s().randombytes_buf(KEY_BYTES);
}
/**
 * Encrypts `plaintext` with `key`. `context` is bound into the ciphertext
 * as associated data (for example the record id), so a ciphertext cannot be
 * moved to a different record without detection.
 */
export function seal(key, plaintext, context) {
    const nonce = s().randombytes_buf(s().crypto_aead_xchacha20poly1305_ietf_NPUBBYTES);
    const ciphertext = s().crypto_aead_xchacha20poly1305_ietf_encrypt(plaintext, context, null, nonce, key);
    return { v: 1, nonce: toBase64(nonce), ciphertext: toBase64(ciphertext) };
}
export function open(key, sealed, context) {
    if (sealed.v !== 1)
        throw new Error(`Unsupported ciphertext version ${String(sealed.v)}`);
    try {
        return s().crypto_aead_xchacha20poly1305_ietf_decrypt(null, fromBase64(sealed.ciphertext), context, fromBase64(sealed.nonce), key);
    }
    catch {
        throw new DecryptionError();
    }
}
export class DecryptionError extends Error {
    constructor() {
        super("Could not decrypt: wrong key, or the data was changed.");
        this.name = "DecryptionError";
    }
}
export function sealJson(key, value, context) {
    return seal(key, new TextEncoder().encode(JSON.stringify(value)), context);
}
export function openJson(key, sealed, context) {
    return JSON.parse(new TextDecoder().decode(open(key, sealed, context)));
}
/**
 * Default cost: 3 passes over 64 MiB. Slow enough to make offline guessing
 * expensive, fast enough (about a second) to unlock on a phone.
 */
export function newKdfParams() {
    return {
        alg: "argon2id13",
        salt: toBase64(s().randombytes_buf(s().crypto_pwhash_SALTBYTES)),
        opsLimit: 3,
        memLimit: 64 * 1024 * 1024,
    };
}
export function deriveKey(passphrase, params) {
    if (params.alg !== "argon2id13")
        throw new Error(`Unsupported KDF ${params.alg}`);
    return s().crypto_pwhash(KEY_BYTES, passphrase.normalize("NFKC"), fromBase64(params.salt), params.opsLimit, params.memLimit, s().crypto_pwhash_ALG_ARGON2ID13);
}
/* ------------------------------------------------------------------ */
/* Recovery key                                                         */
/* ------------------------------------------------------------------ */
const BASE32 = "ABCDEFGHJKLMNPQRSTUVWXYZ23456789"; // No I, O, 0 or 1 to avoid misreading.
/**
 * A random recovery key shown once at setup, written as 13 groups of four
 * characters (256 bits). Spaces, dashes and case are ignored when typed back.
 */
export function newRecoveryKey() {
    const bytes = s().randombytes_buf(KEY_BYTES);
    let bits = 0;
    let value = 0;
    let out = "";
    for (const byte of bytes) {
        value = (value << 8) | byte;
        bits += 8;
        while (bits >= 5) {
            out += BASE32[(value >>> (bits - 5)) & 31];
            bits -= 5;
        }
    }
    if (bits > 0)
        out += BASE32[(value << (5 - bits)) & 31];
    return out.match(/.{1,4}/g).join("-");
}
export function normalizeRecoveryKey(text) {
    return text.toUpperCase().replace(/[^A-Z0-9]/g, "");
}
/** The recovery key is already high-entropy, so a fast hash is enough. */
export function recoveryKeyToKey(recoveryKey) {
    return s().crypto_generichash(KEY_BYTES, `almanac-recovery:${normalizeRecoveryKey(recoveryKey)}`, null);
}
/* ------------------------------------------------------------------ */
/* Key wrapping and the user's key bundle                               */
/* ------------------------------------------------------------------ */
export function wrapKey(kek, key, purpose) {
    return seal(kek, key, `almanac-wrap:${purpose}`);
}
export function unwrapKey(kek, wrapped, purpose) {
    return open(kek, wrapped, `almanac-wrap:${purpose}`);
}
export const AREAS = [
    "budgets",
    "transactions",
    "investments",
    "mortgage",
    "debts",
    "assets",
    "goals",
    "groups",
    "business",
    "events",
];
/** Creates a fresh key hierarchy for a new user. */
export function createAccount(passphrase, areas = AREAS) {
    const master = randomKey();
    const kdf = newKdfParams();
    const recoveryKey = newRecoveryKey();
    const pair = s().crypto_box_keypair();
    const areaKeys = new Map(areas.map((a) => [a, randomKey()]));
    const bundle = {
        version: 1,
        kdf,
        byPassphrase: wrapKey(deriveKey(passphrase, kdf), master, "master"),
        byRecovery: wrapKey(recoveryKeyToKey(recoveryKey), master, "master"),
        publicKey: toBase64(pair.publicKey),
        privateKey: wrapKey(master, pair.privateKey, "private-key"),
        areas: Object.fromEntries([...areaKeys].map(([a, k]) => [a, wrapKey(master, k, `area:${a}`)])),
    };
    return {
        bundle,
        recoveryKey,
        keys: { master, privateKey: pair.privateKey, publicKey: bundle.publicKey, areas: areaKeys },
    };
}
function unlockWithMaster(bundle, master) {
    const areas = new Map();
    for (const [area, wrapped] of Object.entries(bundle.areas)) {
        areas.set(area, unwrapKey(master, wrapped, `area:${area}`));
    }
    return {
        master,
        privateKey: unwrapKey(master, bundle.privateKey, "private-key"),
        publicKey: bundle.publicKey,
        areas,
    };
}
export function unlockWithPassphrase(bundle, passphrase) {
    const master = unwrapKey(deriveKey(passphrase, bundle.kdf), bundle.byPassphrase, "master");
    return unlockWithMaster(bundle, master);
}
export function unlockWithRecoveryKey(bundle, recoveryKey) {
    const master = unwrapKey(recoveryKeyToKey(recoveryKey), bundle.byRecovery, "master");
    return unlockWithMaster(bundle, master);
}
/** Re-wraps the master key under a new passphrase. Data is untouched. */
export function changePassphrase(bundle, keys, newPassphrase) {
    const kdf = newKdfParams();
    return { ...bundle, kdf, byPassphrase: wrapKey(deriveKey(newPassphrase, kdf), keys.master, "master") };
}
/* ------------------------------------------------------------------ */
/* Sharing with connections                                             */
/* ------------------------------------------------------------------ */
/** Seals a key so only the holder of `recipientPublicKey` can open it. */
export function shareKey(key, recipientPublicKey) {
    return toBase64(s().crypto_box_seal(key, fromBase64(recipientPublicKey)));
}
export function receiveSharedKey(sealedKey, keys) {
    try {
        return s().crypto_box_seal_open(fromBase64(sealedKey), fromBase64(keys.publicKey), keys.privateKey);
    }
    catch {
        throw new DecryptionError();
    }
}
/** Generates a replacement key for an area, used when revoking access. */
export function rotateAreaKey(bundle, keys, area) {
    const key = randomKey();
    keys.areas.set(area, key);
    return {
        key,
        bundle: { ...bundle, areas: { ...bundle.areas, [area]: wrapKey(keys.master, key, `area:${area}`) } },
    };
}
//# sourceMappingURL=index.js.map